import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { CheckCircle, AlertCircle, RefreshCw, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { 
  extractRequestToken,
  exchangeToken,
  getAuthState,
  getLastAuthError,
  clearLastAuthError
} from '@/utils/kiteConnect';

export default function KiteCallback() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userData, setUserData] = useState<any>(null);

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Check for existing auth error
      const authError = getLastAuthError();
      if (authError) {
        setError(authError);
        clearLastAuthError();
        setIsLoading(false);
        return;
      }

      // Extract request token from URL
      const requestToken = extractRequestToken();
      
      if (!requestToken) {
        // Check if user is already authenticated
        const currentAuthState = getAuthState();
        if (currentAuthState.isAuthenticated) {
          setSuccess(true);
          setUserData(currentAuthState.userData);
          toast.success('Already connected to Zerodha');
          
          // Redirect to dashboard after a short delay
          setTimeout(() => {
            navigate('/dashboard');
          }, 2000);
          return;
        }
        
        throw new Error('No request token found in URL');
      }

      console.log('Processing request token:', requestToken);
      
      // Exchange request token for access token
      const result = await exchangeToken(requestToken);
      
      if (result.success) {
        setSuccess(true);
        setUserData(result);
        toast.success('Successfully connected to Zerodha!');
        
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        throw new Error(result.error || 'Token exchange failed');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Authentication failed';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Callback error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = () => {
    handleCallback();
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleBackToProfile = () => {
    navigate('/profile-setup');
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center gap-2">
            {isLoading && <RefreshCw className="h-5 w-5 animate-spin" />}
            {success && <CheckCircle className="h-5 w-5 text-green-600" />}
            {error && <AlertCircle className="h-5 w-5 text-red-600" />}
            Zerodha Connection
          </CardTitle>
          <CardDescription>
            {isLoading && 'Processing your Zerodha connection...'}
            {success && 'Successfully connected to Zerodha!'}
            {error && 'Connection failed'}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {isLoading && (
            <div className="text-center space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-800">
                  Please wait while we complete your Zerodha connection...
                </p>
              </div>
              <div className="flex justify-center">
                <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            </div>
          )}

          {success && userData && (
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <div>
                    <h4 className="font-medium text-green-900">
                      Connection Successful!
                    </h4>
                    <p className="text-sm text-green-700">
                      Welcome, {userData.userName || userData.userShortname || 'User'}!
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">What's Next?</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Access your portfolio data</li>
                  <li>• Get AI-powered investment insights</li>
                  <li>• Track your portfolio performance</li>
                </ul>
              </div>

              <p className="text-sm text-center text-gray-600">
                Redirecting to dashboard in a few seconds...
              </p>
            </div>
          )}

          {error && (
            <div className="space-y-4">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {error}
                </AlertDescription>
              </Alert>

              <div className="bg-yellow-50 p-4 rounded-lg">
                <h4 className="font-medium text-yellow-900 mb-2">
                  Common Issues
                </h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Check your internet connection</li>
                  <li>• Ensure you have an active Zerodha account</li>
                  <li>• Try clearing browser cache and cookies</li>
                  <li>• Make sure popups are not blocked</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <Button 
                  onClick={handleRetry}
                  variant="outline"
                  className="flex-1"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button 
              onClick={handleBackToDashboard}
              variant="outline"
              className="flex-1"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Dashboard
            </Button>
            
            <Button 
              onClick={handleBackToProfile}
              variant="outline"
              className="flex-1"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Profile Setup
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}